{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2016 - 2021                               }
{            Email : info@tmssoftware.com                            }
{            Web : https://www.tmssoftware.com                       }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.TMSFNCRadioGroup;

{$I WEBLib.TMSFNCDefines.inc}

{$IFDEF WEBLIB}
{$DEFINE LCLWEBLIB}
{$DEFINE CMNWEBLIB}
{$ENDIF}
{$IFDEF CMNLIB}
{$DEFINE CMNWEBLIB}
{$ENDIF}
{$IFDEF LCLLIB}
{$DEFINE LCLWEBLIB}
{$ENDIF}

interface

uses
  Classes, WEBLib.TMSFNCCustomControl, WEBLib.TMSFNCCustomGroup, WEBLib.TMSFNCGraphicsTypes,
  WEBLib.TMSFNCTypes, WEBLib.StdCtrls, WEBLib.Controls, WEBLib.TMSFNCHTMLText
  {$IFNDEF LCLLIB}
  ,Types
  {$ENDIF}
  ;

const
  MAJ_VER = 1; // Major version nr.
  MIN_VER = 0; // Minor version nr.
  REL_VER = 3; // Release nr.
  BLD_VER = 3; // Build nr.

  // version history
  // v1.0.0.0 : first release
  // v1.0.1.0 : New : OnTitleAnchorClick and OnRadioButonAnchorClick events implemented
  // v1.0.1.1 : Fixed : Double clicking behavior on item anchors
  // v1.0.1.2 : Fixed : Issue with OnRadioButtonClick event triggered twice
  // v1.0.1.3 : Fixed : Issue with Anchor if font was changed
  // v1.0.1.4 : Fixed : Issue in Delphi 11 with begin and end scene for CreateBitmapCanvas
  // v1.0.2.0 : New : OnRadioButtonDblClick event added
  // v1.0.3.0 : New : Support for high dpi
  // v1.0.3.1 : Fixed : Introducing RadioTabStop for handling tabs when focusing a radiobutton with the mouse
  // v1.0.3.2 : Fixed : Issue with OnClick, OnEnter and OnExit events not being triggered
  // v1.0.3.3 : Fixed : Issue with initializing picker in FMX

type
  TTMSFNCRadioHTMLText = class(TTMSFNCHTMLText)
  protected
    function CanDrawDesignTime: Boolean; override;
  end;

  TTMSFNCRadioContainer = class(TTMSFNCCustomControl)
  private
    FRadioButton: TRadioButton;
    {$IFDEF LCLLIB}
    FHiddenRadioButton: TRadioButton;
    {$ENDIF}
    FHTMLText: TTMSFNCRadioHTMLText;
  protected
    procedure RegisterRuntimeClasses; override;
  public
    constructor Create(AOwner: TComponent); override;
    destructor Destroy; override;
    property HTMLText: TTMSFNCRadioHTMLText read FHTMLText;
    property RadioButton: TRadioButton read FRadioButton;
  end;

  TTMSFNCRadioGroupTitleCheckBoxMode = (rgtcmNone, rgtcmToggleEnabled);

  TTMSFNCRadioGroupTitleSetCheckState = procedure(Sender: TObject; ACheckState: Boolean) of object;
  TTMSFNCRadioGroupTitleGetCheckState = procedure(Sender: TObject; var ACheckState: Boolean) of object;

  TTMSFNCRadioGroupClickEvent = procedure(Sender: TObject; AItemIndex: Integer) of object;
  TTMSFNCRadioGroupTitleAnchorClickEvent = procedure(Sender: TObject; AAnchor: string) of object;
  TTMSFNCRadioGroupItemAnchorClickEvent = procedure(Sender: TObject; AItemIndex: Integer; AAnchor: string) of object;

  TTMSFNCRadioGroupTitle = class(TTMSFNCGroupTitle)
  private
    FCheckBoxMode: TTMSFNCRadioGroupTitleCheckBoxMode;
    FShowCheckBox: Boolean;
    FOnGetCheckState: TTMSFNCRadioGroupTitleGetCheckState;
    FOnSetCheckState: TTMSFNCRadioGroupTitleSetCheckState;
    procedure SetCheckBoxMode(const Value: TTMSFNCRadioGroupTitleCheckBoxMode);
    procedure SetShowCheckBox(const Value: Boolean);
    function GetChecked: Boolean;
    procedure SetChecked(const Value: Boolean);
  protected
    property OnSetCheckState: TTMSFNCRadioGroupTitleSetCheckState read FOnSetCheckState write FOnSetCheckState;
    property OnGetCheckState: TTMSFNCRadioGroupTitleGetCheckState read FOnGetCheckState write FOnGetCheckState;
  public
    constructor Create; override;
    procedure Assign(Source: TPersistent); override;
  published
    property ShowCheckBox: Boolean read FShowCheckBox write SetShowCheckBox default False;
    property Checked: Boolean read GetChecked write SetChecked default True;
    property CheckBoxMode: TTMSFNCRadioGroupTitleCheckBoxMode read FCheckBoxMode write SetCheckBoxMode default rgtcmToggleEnabled;
  end;

  {$IFNDEF LCLLIB}
  [ComponentPlatformsAttribute(TMSPlatformsWeb)]
  {$ENDIF}
  TTMSFNCRadioGroup = class(TTMSFNCCustomGroup)
  private
    FItemIndex: Integer;
    FTitleCheckBox: TCheckBox;
    FOnTitleCheckBoxClick: TNotifyEvent;
    FOnRadioButtonClick: TTMSFNCRadioGroupClickEvent;
    FOnTitleAnchorClick: TTMSFNCRadioGroupTitleAnchorClickEvent;
    FOnRadioButtonAnchorClick: TTMSFNCRadioGroupItemAnchorClickEvent;
    FOnRadioButtonDblClick: TTMSFNCRadioGroupClickEvent;
    FRadioTabStop: Boolean;
    function GetRadioButton(AIndex: Integer): TRadioButton;
    procedure SetItemIndex(Value: Integer);
    function GetHTMLText(AIndex: Integer): TTMSFNCRadioHTMLText;
    function GetRadioContainer(AIndex: Integer): TTMSFNCRadioContainer;
    function GetTitle: TTMSFNCRadioGroupTitle;
    procedure SetTitle(const Value: TTMSFNCRadioGroupTitle);
    procedure SetRadioTabStop(const Value: Boolean);
  protected
    procedure RegisterRuntimeClasses; override;
    procedure ControlClick(Sender: TObject); virtual;
    procedure ControlEnter(Sender: TObject); virtual;
    procedure ControlExit(Sender: TObject); virtual;
    procedure ControlDblClick(Sender: TObject); virtual;
    procedure AnchorClick(Sender: TObject; AAnchor: string);
    procedure ArrangeControls; override;
    procedure ItemsChanged; override;
    procedure UpdateItemIndexControl; override;
    procedure SetControlText(AControl: TControl; AIndex: Integer; AText: string); override;
    procedure SetControlCheckState(AControl: TControl; ACheckState: Boolean); virtual;
    procedure SetControlProperties(AControl: TControl; AIndex: Integer); override;
    procedure UpdateControlProperties(AControl: TControl; AIndex: Integer); override;
    procedure TitleCheckBoxChange(Sender: TObject);
    procedure CreateAdditionalControls; override;
    procedure SetControlEnabled(AControl: TControl; AIndex: Integer); override;
    procedure SetControlName(AControl: TControl; AIndex: Integer); override;
    procedure DoSetTitleCheckState(Sender: TObject; ACheckState: Boolean); virtual;
    procedure DoGetTitleCheckState(Sender: TObject; var ACheckState: Boolean); virtual;
    procedure InitializeProperties; override;
    {$IFDEF LCLLIB}
    procedure InitializeWnd; override;
    {$ENDIF}
    function CanDrawTitle: Boolean; override;
    function GetVersion: string; override;
    function CreateGroupTitle: TTMSFNCGroupTitle; override;
    function GetTitleRect: TRectF; override;
    function GetTitleDrawRect(ARect: TRectF): TRectF; override;
    function GetControlText(AControl: TControl; AIndex: Integer): string; override;
    function GetControlClass({%H-}AIndex: Integer): TTMSFNCCustomGroupControlClass; override;
    function GetControlCheckState(AControl: TControl): Boolean; virtual;
    procedure HandleMouseUp(Button: TTMSFNCMouseButton; Shift: TShiftState; X, Y: Single); override;
    procedure HandleMouseMove(Shift: TShiftState; X, Y: Single); override;
    function XYtoTitleAnchor(AX: Single; AY: Single): string;
    procedure DoTitleAnchorClick(AAnchor: string); virtual;
    procedure DoRadioButtonAnchorClick(AItemIndex: Integer; AAnchor: string); virtual;
    procedure Loaded; override;
  public
    destructor Destroy; override;
    constructor Create(AOwner: TComponent); override;
    procedure Assign(Source: TPersistent); override;
    property RadioButtons[AIndex: Integer]: TRadioButton read GetRadioButton;
    property Containers[AIndex: Integer]: TTMSFNCRadioContainer read GetRadioContainer;
    property HTMLTexts[AIndex: Integer]: TTMSFNCRadioHTMLText read GetHTMLText;
    property Texts;
  published
    property RadioTabStop: Boolean read FRadioTabStop write SetRadioTabStop default True;
    property BitmapContainer;
    property Version: String read GetVersion;
    property Items;
    property Font;
    property Columns;
    property Fill;
    property Stroke;
    property ItemIndex: Integer read FItemIndex write SetItemIndex default -1;
    property Title: TTMSFNCRadioGroupTitle read GetTitle write SetTitle;
    property OnTitleCheckBoxClick: TNotifyEvent read FOnTitleCheckBoxClick write FOnTitleCheckBoxClick;
    property OnRadioButtonClick: TTMSFNCRadioGroupClickEvent read FOnRadioButtonClick write FOnRadioButtonClick;
    property OnRadioButtonDblClick: TTMSFNCRadioGroupClickEvent read FOnRadioButtonDblClick write FOnRadioButtonDblClick;
    property OnTitleAnchorClick: TTMSFNCRadioGroupTitleAnchorClickEvent read FOnTitleAnchorClick write FOnTitleAnchorClick;
    property OnRadioButtonAnchorClick: TTMSFNCRadioGroupItemAnchorClickEvent read FOnRadioButtonAnchorClick write FOnRadioButtonAnchorClick;
  end;

implementation

uses
  WEBLib.TMSFNCUtils, WEBLib.TMSFNCGraphics
  {$IFNDEF LCLWEBLIB}
  , UITypes
  {$ENDIF}
  {$IFDEF FMXLIB}
  ,FMX.Types
  {$ENDIF}
  ;

procedure TTMSFNCRadioGroup.AnchorClick(Sender: TObject; AAnchor: string);
var
  I: integer;
begin
  if (Sender is TRadioButton) or (Sender is TTMSFNCRadioHTMLText) then
    I := ControlsList.IndexOf((Sender as TControl).Parent as TControl)
  else
    I := ControlsList.IndexOf(Sender as TControl);

  DoRadioButtonAnchorClick(I, AAnchor);
end;

procedure TTMSFNCRadioGroup.ArrangeControls;
var
  r: TRectF;
begin
  inherited;
  if Assigned(FTitleCheckBox) then
  begin
    FTitleCheckBox.Visible := Title.ShowCheckBox;
    if FTitleCheckBox.Visible then
    begin
      FTitleCheckBox.Parent := Self;
      r := GetTitleDrawRect(LocalRect);
      {$IFDEF FMXLIB}
      FTitleCheckBox.Position.X := Round(r.Left - FTitleCheckBox.Width);
      FTitleCheckBox.Position.Y := Round(r.Top + ((r.Bottom - r.Top) - FTitleCheckBox.Height) / 2);
      {$ENDIF}
      {$IFDEF CMNWEBLIB}
      FTitleCheckBox.Left := Round(r.Left - FTitleCheckBox.Width);
      FTitleCheckBox.Top := Round(r.Top + ((r.Bottom - r.Top) - FTitleCheckBox.Height) / 2);
      {$ENDIF}
    end
    else
      FTitleCheckBox.Parent := nil;
  end;
end;

constructor TTMSFNCRadioGroup.Create(AOwner: TComponent);
begin
  inherited;
  FRadioTabStop := True;
end;

procedure TTMSFNCRadioGroup.CreateAdditionalControls;
begin
  inherited;
  FTitleCheckBox := TCheckBox.Create(Self);
  FTitleCheckBox.Visible := False;
  FTitleCheckBox.Width := FTitleCheckBox.Height;
  {$IFDEF FMXLIB}
  FTitleCheckBox.Text := '';
  FTitleCheckBox.IsChecked := True;
  FTitleCheckBox.Stored := False;
  FTitleCheckBox.OnChange := @TitleCheckBoxChange;
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  FTitleCheckBox.Caption := '';
  FTitleCheckBox.Checked := True;
  FTitleCheckBox.OnClick := TitleCheckBoxChange;
  {$ENDIF}
end;

function TTMSFNCRadioGroup.CreateGroupTitle: TTMSFNCGroupTitle;
begin
  Result := TTMSFNCRadioGroupTitle.Create;
  (Result as TTMSFNCRadioGroupTitle).OnSetCheckState := DoSetTitleCheckState;
  (Result as TTMSFNCRadioGroupTitle).OnGetCheckState := DoGetTitleCheckState;
end;

destructor TTMSFNCRadioGroup.Destroy;
begin
  if Assigned(FTitleCheckBox) then
    FTitleCheckBox.Free;

  inherited;
end;

procedure TTMSFNCRadioGroup.DoGetTitleCheckState(Sender: TObject;
  var ACheckState: Boolean);
begin
  if Assigned(FTitleCheckBox) then
  begin
    {$IFDEF FMXLIB}
    ACheckState := FTitleCheckBox.IsChecked;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    ACheckState := FTitleCheckBox.Checked;
    {$ENDIF}
  end;
end;

procedure TTMSFNCRadioGroup.DoRadioButtonAnchorClick(AItemIndex: Integer; AAnchor: string);
begin
  if Assigned(OnRadioButtonAnchorClick) then
    OnRadioButtonAnchorClick(Self, AItemIndex, AAnchor)
  else
    TTMSFNCUtils.OpenURL(AAnchor);
end;

procedure TTMSFNCRadioGroup.DoSetTitleCheckState(Sender: TObject;
  ACheckState: Boolean);
var
  evnt: TNotifyEvent;
begin
  if Assigned(FTitleCheckBox) then
  begin
    {$IFDEF FMXLIB}
    evnt := FTitleCheckBox.OnChange;
    FTitleCheckBox.OnChange := nil;
    FTitleCheckBox.IsChecked := ACheckState;
    FTitleCheckBox.OnChange := @evnt;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    evnt := FTitleCheckBox.OnClick;
    FTitleCheckBox.OnClick := nil;
    FTitleCheckBox.Checked := ACheckState;
    FTitleCheckBox.OnClick := evnt;
    {$ENDIF}
  end;
end;

procedure TTMSFNCRadioGroup.DoTitleAnchorClick(AAnchor: string);
begin
  if Assigned(OnTitleAnchorClick) then
    OnTitleAnchorClick(Self, AAnchor)
  else
    TTMSFNCUtils.OpenURL(AAnchor);
end;

procedure TTMSFNCRadioGroup.Assign(Source: TPersistent);
begin
  inherited;
  if (Source is TTMSFNCRadioGroup) then
    FItemIndex := (Source as TTMSFNCRadioGroup).ItemIndex;
end;

function TTMSFNCRadioGroup.CanDrawTitle: Boolean;
begin
  Result := inherited CanDrawTitle;
  Result := Result or Title.ShowCheckBox;
end;

procedure TTMSFNCRadioGroup.ControlClick(Sender: TObject);
var
  I: Integer;
begin
  if not IsUpdatingControl then
  begin
    if (Sender is TRadioButton) or (Sender is TTMSFNCRadioHTMLText) then
      I := ControlsList.IndexOf((Sender as TControl).Parent as TControl)
    else
      I := ControlsList.IndexOf(Sender as TControl);

    if Assigned(Containers[I]) then
    begin
      if Containers[I].FRadioButton.Enabled then
      begin
        ItemIndex := I;

        if Assigned(Containers[I]) then
          Containers[I].FRadioButton.SetFocus;

        if Assigned(OnRadioButtonClick) then
          OnRadioButtonClick(Self, ItemIndex);
      end;
    end;
  end;

  Click;
end;

procedure TTMSFNCRadioGroup.ControlDblClick(Sender: TObject);
var
  I: Integer;
begin
  if not IsUpdatingControl then
  begin
    if (Sender is TRadioButton) or (Sender is TTMSFNCRadioHTMLText) then
      I := ControlsList.IndexOf((Sender as TControl).Parent as TControl)
    else
      I := ControlsList.IndexOf(Sender as TControl);

    if Assigned(Containers[I]) then
    begin
      if Containers[I].FRadioButton.Enabled then
      begin
        ItemIndex := I;

        if Assigned(Containers[I]) then
          Containers[I].FRadioButton.SetFocus;

        if Assigned(OnRadioButtonDblClick) then
          OnRadioButtonDblClick(Self, ItemIndex);
      end;
    end;
  end;

  DblClick;
end;

procedure TTMSFNCRadioGroup.ControlEnter(Sender: TObject);
begin
  DoEnter;
end;

procedure TTMSFNCRadioGroup.ControlExit(Sender: TObject);
begin
  DoExit;
end;

function TTMSFNCRadioGroup.GetTitle: TTMSFNCRadioGroupTitle;
begin
  Result := TTMSFNCRadioGroupTitle(inherited Title);
end;

function TTMSFNCRadioGroup.GetTitleDrawRect(ARect: TRectF): TRectF;
begin
  Result := inherited GetTitleDrawRect(ARect);
  if not Title.ShowCheckBox then
    Exit;

  Result.Left := Result.Left + FTitleCheckBox.Width + 4;
end;

function TTMSFNCRadioGroup.GetTitleRect: TRectF;
begin
  Result := inherited GetTitleRect;
  if not Title.ShowCheckBox then
    Exit;

  Result.Right := Result.Right + FTitleCheckBox.Width;
  if FTitleCheckBox.Height + 4 > Result.Bottom - Result.Top then
    Result.Bottom := Result.Top + FTitleCheckBox.Height + 4;
end;

function TTMSFNCRadioGroup.GetControlCheckState(AControl: TControl): Boolean;
begin
  Result := False;
  if Assigned(AControl) and (AControl is TTMSFNCRadioContainer) then
  begin
    {$IFDEF FMXLIB}
    Result := (AControl as TTMSFNCRadioContainer).FRadioButton.IsChecked;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    Result := (AControl as TTMSFNCRadioContainer).FRadioButton.Checked;
    {$ENDIF}
  end;
end;

function TTMSFNCRadioGroup.GetControlClass(AIndex: Integer): TTMSFNCCustomGroupControlClass;
begin
  Result := TTMSFNCRadioContainer;
end;

function TTMSFNCRadioGroup.GetControlText(AControl: TControl; AIndex: Integer): String;
begin
  Result := inherited GetControlText(AControl, AIndex);
  if Assigned(AControl) and (AControl is TTMSFNCRadioContainer) then
    Result := (AControl as TTMSFNCRadioContainer).FHTMLText.Text;
end;

function TTMSFNCRadioGroup.GetHTMLText(AIndex: Integer): TTMSFNCRadioHTMLText;
var
  c: TControl;
begin
  Result := nil;
  c := Controls[AIndex];
  if Assigned(c) and (c is TTMSFNCRadioContainer) then
    Result := (c as TTMSFNCRadioContainer).FHTMLText;
end;

function TTMSFNCRadioGroup.GetRadioButton(AIndex: Integer): TRadioButton;
var
  c: TControl;
begin
  Result := nil;
  c := Controls[AIndex];
  if Assigned(c) and (c is TTMSFNCRadioContainer) then
    Result := (c as TTMSFNCRadioContainer).FRadioButton;
end;

function TTMSFNCRadioGroup.GetRadioContainer(
  AIndex: Integer): TTMSFNCRadioContainer;
var
  c: TControl;
begin
  Result := nil;
  c := Controls[AIndex];
  if Assigned(c) and (c is TTMSFNCRadioContainer) then
    Result := (c as TTMSFNCRadioContainer);
end;

function TTMSFNCRadioGroup.GetVersion: string;
begin
  Result := GetVersionNumber(MAJ_VER, MIN_VER, REL_VER, BLD_VER);
end;

procedure TTMSFNCRadioGroup.HandleMouseMove(Shift: TShiftState; X, Y: Single);
var
  a: string;
begin
  a := XYtoTitleAnchor(X,Y);
  if a <> '' then
    Cursor := crHandPoint
  else
    Cursor := crDefault;

  inherited;
end;

procedure TTMSFNCRadioGroup.HandleMouseUp(Button: TTMSFNCMouseButton;
  Shift: TShiftState; X, Y: Single);
var
  a: string;
begin
  inherited;

  a := XYtoTitleAnchor(X,Y);
  if a <> '' then
    DoTitleAnchorClick(a)
end;

procedure TTMSFNCRadioGroup.InitializeProperties;
begin
  inherited;
  FItemIndex := -1;
end;

{$IFDEF LCLLIB}
procedure TTMSFNCRadioGroup.InitializeWnd;
var
  I: Integer;
begin
  inherited;
  for I := 0 to ControlsList.Count - 1 do
  begin
    if HandleAllocated then
      (Controls[I] as TTMSFNCRadioContainer).FHiddenRadioButton.HandleNeeded;

    (Controls[I] as TTMSFNCRadioContainer).FHiddenRadioButton.Checked := True;
  end;
end;
{$ENDIF}

procedure TTMSFNCRadioGroup.ItemsChanged;
begin
  if FItemIndex >= Items.Count then
    FItemIndex := Items.Count - 1;
end;

procedure TTMSFNCRadioGroup.Loaded;
begin
  inherited;
  {$IFDEF VCLLIB}
  {$HINTS OFF}
  {$IF (COMPILERVERSION >= 33) AND (COMPILERVERSION < 36)}
  FTitleCheckBox.ScaleForPPI(CurrentPPI);
  {$IFEND}
  {$HINTS ON}
  {$ENDIF}
end;

procedure TTMSFNCRadioGroup.RegisterRuntimeClasses;
begin
  inherited;
  RegisterClass(TTMSFNCRadioGroup);
end;

procedure TTMSFNCRadioGroup.SetControlCheckState(AControl: TControl;
  ACheckState: Boolean);
var
  evnt: TNotifyEvent;
begin
  if Assigned(AControl) and (AControl is TTMSFNCRadioContainer) then
  begin
    evnt := (AControl as TTMSFNCRadioContainer).FRadioButton.OnClick;
    (AControl as TTMSFNCRadioContainer).FRadioButton.OnClick := nil;
    {$IFDEF FMXLIB}
    (AControl as TTMSFNCRadioContainer).FRadioButton.IsChecked := ACheckState;
    {$ENDIF}
    {$IFDEF CMNWEBLIB}
    (AControl as TTMSFNCRadioContainer).FRadioButton.Checked := ACheckState;
    {$IFDEF LCLLIB}
    (AControl as TTMSFNCRadioContainer).FHiddenRadioButton.Checked := not ACheckState;
    {$ENDIF}
    {$ENDIF}
    (AControl as TTMSFNCRadioContainer).FRadioButton.OnClick := evnt;

    if ACheckState and not RadioTabStop then
      (AControl as TTMSFNCRadioContainer).FRadioButton.TabStop := False;
  end;
end;

procedure TTMSFNCRadioGroup.SetControlEnabled(AControl: TControl;
  AIndex: Integer);
begin
  inherited;
  if Assigned(AControl) and Title.ShowCheckBox and (Title.CheckBoxMode = rgtcmToggleEnabled) then
    AControl.Enabled := Title.Checked;

  if AControl is TTMSFNCRadioContainer then
  begin
    (AControl as TTMSFNCRadioContainer).HTMLText.Enabled := Enabled;
    (AControl as TTMSFNCRadioContainer).RadioButton.Enabled := Enabled;
  end;
end;

procedure TTMSFNCRadioGroup.SetTitle(const Value: TTMSFNCRadioGroupTitle);
begin
  Title.Assign(Value);
end;

procedure TTMSFNCRadioGroup.SetControlName(AControl: TControl; AIndex: Integer);
begin
  inherited;
  if Assigned(AControl) and (AControl is TTMSFNCRadioContainer) then
  begin
    {$IFDEF FMXLIB}
    (AControl as TTMSFNCRadioContainer).FRadioButton.GroupName := Name;
    {$ENDIF}
  end;
end;

procedure TTMSFNCRadioGroup.SetControlProperties(AControl: TControl; AIndex: Integer);
begin
  inherited;
  if Assigned(AControl) and (AControl is TTMSFNCRadioContainer) then
  begin
    (AControl as TTMSFNCRadioContainer).FRadioButton.OnClick := ControlClick;
    {$IFDEF FMXLIB}
    (AControl as TTMSFNCRadioContainer).FRadioButton.OnDblClick := ControlDblClick;
    {$ENDIF}
    (AControl as TTMSFNCRadioContainer).FRadioButton.OnEnter := @ControlEnter;
    (AControl as TTMSFNCRadioContainer).FRadioButton.OnExit := @ControlExit;
    (AControl as TTMSFNCRadioContainer).FHTMLText.OnClick := ControlClick;
    (AControl as TTMSFNCRadioContainer).FHTMLText.OnDblClick := ControlDblClick;
    (AControl as TTMSFNCRadioContainer).FHTMLText.OnAnchorClick := @AnchorClick;
    (AControl as TTMSFNCRadioContainer).FHTMLText.OnEnter := @ControlEnter;
    (AControl as TTMSFNCRadioContainer).FHTMLText.OnExit := @ControlExit;

    (AControl as TTMSFNCRadioContainer).OnClick := ControlClick;
    (AControl as TTMSFNCRadioContainer).OnEnter := @ControlEnter;
    (AControl as TTMSFNCRadioContainer).OnExit := @ControlExit;
    (AControl as TTMSFNCRadioContainer).OnDblClick := ControlDblClick;
    (AControl as TTMSFNCRadioContainer).FHTMLText.Font.Assign(Font);
  end;
end;

procedure TTMSFNCRadioGroup.SetControlText(AControl: TControl; AIndex: Integer; AText: String);
begin
  inherited;
  if Assigned(AControl) and (AControl is TTMSFNCRadioContainer) then
    (AControl as TTMSFNCRadioContainer).FHTMLText.Text := AText;
end;

procedure TTMSFNCRadioGroup.TitleCheckBoxChange(Sender: TObject);
begin
  if Title.ShowCheckBox then
  begin
    case Title.CheckBoxMode of
      rgtcmToggleEnabled:
      begin
        ArrangeControls;
        Invalidate;
      end;
    end;

    if Assigned(OnTitleCheckBoxClick) then
      OnTitleCheckBoxClick(Self);
  end;
end;

procedure TTMSFNCRadioGroup.UpdateControlProperties(AControl: TControl; AIndex: Integer);
begin
  inherited;
  if Assigned(AControl) and (AControl is TTMSFNCRadioContainer) then
  begin
    (AControl as TTMSFNCRadioContainer).FHTMLText.Font.Assign(Font);
    (AControl as TTMSFNCRadioContainer).FHTMLText.BitmapContainer := BitmapContainer;
  end;
end;

procedure TTMSFNCRadioGroup.UpdateItemIndexControl;
begin
  if FItemIndex >= 0 then
    SetControlCheckState(Controls[FItemIndex], True);
end;

function TTMSFNCRadioGroup.XYtoTitleAnchor(AX, AY: Single): string;
var
  r: TRectF;
  g: TTMSFNCGraphics;
begin
  Result := '';
  r := GetTitleDrawRect(GetTitleRect);

  g := TTMSFNCGraphics.CreateBitmapCanvas;
  g.BeginScene;
  g.BitmapContainer := BitmapContainer;
  try
    g.Font.Assign(Title.Font);
    Result := g.DrawText(r, Title.Text, false, gtaCenter, gtaCenter, gttNone, 0, -1, -1, True, True, AX, AY);
  finally
    g.EndScene;
    g.Free;
  end;
end;

procedure TTMSFNCRadioGroup.SetItemIndex(Value: Integer);
begin
  if IsReadingControl then
    FItemIndex := Value
  else
  begin
    if Value < -1 then
      Value := -1;

    if Value >= ControlsList.Count then
      Value := ControlsList.Count - 1;

    if FItemIndex <> Value then
    begin
      if FItemIndex >= 0 then
        SetControlCheckState(Controls[FItemIndex], False);
      FItemIndex := Value;
      if FItemIndex >= 0 then
        SetControlCheckState(Controls[FItemIndex], True);
    end;
  end;
end;

procedure TTMSFNCRadioGroup.SetRadioTabStop(const Value: Boolean);
var
  c: TControl;
begin
  if Value <> FRadioTabStop then
  begin
    FRadioTabStop := Value;
    if [csDesigning, csLoading, csReading] * ComponentState = [] then
    begin
      if FItemIndex >= 0 then
      begin
        c := Controls[FItemIndex];
        if Assigned(c) and (c is TTMSFNCRadioContainer) then
          (c as TTMSFNCRadioContainer).FRadioButton.TabStop := FRadioTabStop;
      end;
    end;
  end;
end;

{ TTMSFNCRadioContainer }

constructor TTMSFNCRadioContainer.Create(AOwner: TComponent);
begin
  inherited;
  DisableBackground;
  TabStop := False;
  {$IFDEF LCLLIB}
  FHiddenRadioButton := TRadioButton.Create(Self);
  FHiddenRadioButton.Name := 'HiddenRadioButton';
  FHiddenRadioButton.Parent := Self;
  FHiddenRadioButton.Visible := False;
  FHiddenRadioButton.ControlStyle := FHiddenRadioButton.ControlStyle + [csNoDesignSelectable, csNoDesignVisible];
  {$ENDIF}
  FRadioButton := TRadioButton.Create(Self);
  FRadioButton.Parent := Self;
  {$IFDEF FMXMOBILE}
  FRadioButton.Width := 25;
  FRadioButton.Height := 25;
  {$ELSE}
  FRadioButton.Width := FRadioButton.Height;
  {$ENDIF}
  Height := FRadioButton.Height;
  FHTMLText := TTMSFNCRadioHTMLText.Create(Self);
  FHTMLText.AllowFocus := False;
  FHTMLText.Parent := Self;
  {$IFDEF FMXMOBILE}
  FHTMLText.Margins.Left := 3;
  {$ENDIF}
  FHTMLText.DisableBackground;
  FHTMLText.HorizontalTextAlign := gtaLeading;
  {$IFDEF FMXLIB}
  FHTMLText.Align := TAlignLayout.Client;
  FRadioButton.Align := TAlignLayout.Left;
  {$ENDIF}
  {$IFDEF CMNWEBLIB}
  ParentColor := True;
  FHTMLText.ParentColor := True;
  {$IFDEF VCLLIB}
  ParentBackground := True;
  FHTMLText.ParentBackground := True;
  {$ENDIF}
  FHTMLText.Align := alClient;
  FRadioButton.Align := alLeft;
  {$ENDIF}
end;

destructor TTMSFNCRadioContainer.Destroy;
begin
  {$IFDEF LCLLIB}
  if Assigned(FHiddenRadioButton) then
    FHiddenRadioButton.Free;
  {$ENDIF}

  if Assigned(FRadioButton) then
    FRadioButton.Free;

  if Assigned(FHTMLText) then
    FHTMLText.Free;
  inherited;
end;

procedure TTMSFNCRadioContainer.RegisterRuntimeClasses;
begin
  inherited;
  RegisterClass(TTMSFNCRadioContainer);
end;

{ TTMSFNCRadioGroupTitle }

procedure TTMSFNCRadioGroupTitle.Assign(Source: TPersistent);
begin
  inherited;
  if (Source is TTMSFNCRadioGroupTitle) then
  begin
    FCheckBoxMode := (Source as TTMSFNCRadioGroupTitle).CheckBoxMode;
    FShowCheckBox := (Source as TTMSFNCRadioGroupTitle).ShowCheckBox;
    Checked := (Source as TTMSFNCRadioGroupTitle).Checked;
  end;
end;

constructor TTMSFNCRadioGroupTitle.Create;
begin
  inherited;
  FShowCheckBox := False;
  FCheckBoxMode := rgtcmToggleEnabled;
end;

function TTMSFNCRadioGroupTitle.GetChecked: Boolean;
var
  b: Boolean;
begin
  b := False;
  if Assigned(OnGetCheckState) then
    OnGetCheckState(Self, b);

  Result := b;
end;

procedure TTMSFNCRadioGroupTitle.SetCheckBoxMode(
  const Value: TTMSFNCRadioGroupTitleCheckBoxMode);
begin
  if FCheckBoxMode <> Value then
  begin
    FCheckBoxMode := Value;
    Changed;
  end;
end;

procedure TTMSFNCRadioGroupTitle.SetChecked(const Value: Boolean);
begin
  if Assigned(OnSetCheckState) then
    OnSetCheckState(Self, Value);
end;

procedure TTMSFNCRadioGroupTitle.SetShowCheckBox(const Value: Boolean);
begin
  if FShowCheckBox <> Value then
  begin
    FShowCheckBox := Value;
    Changed;
  end;
end;

{ TTMSFNCRadioHTMLText }

function TTMSFNCRadioHTMLText.CanDrawDesignTime: Boolean;
begin
  Result := False;
end;

end.
